import { Component } from 'react';
import { AlertTriangle, RotateCcw, RefreshCw } from 'lucide-react';

/**
 * React Error Boundary — catches render-time exceptions in the subtree.
 * Must be a class component; React does not support error boundaries as
 * function components.
 *
 * Props:
 *   children  — the subtree to guard
 *   fallback  — optional custom fallback element (overrides the default card)
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.resetBoundary = this.resetBoundary.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info);
  }

  resetBoundary() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return children;
    }

    if (fallback) {
      return fallback;
    }

    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4 py-12 bg-light-bg dark:bg-dark-bg">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border shadow-subtle rounded-sm p-8">
            {/* Icon */}
            <div className="flex justify-center mb-5">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" aria-hidden="true" />
              </div>
            </div>

            {/* Heading */}
            <h1 className="text-xl font-semibold text-center text-light-text dark:text-dark-text mb-2 tracking-tight">
              Something went wrong
            </h1>

            {/* Message */}
            <p className="text-center text-slate-600 dark:text-dark-text-soft text-sm leading-relaxed mb-1">
              An unexpected error occurred while rendering this page.
            </p>
            <p className="text-center text-slate-600 dark:text-dark-text-soft text-sm leading-relaxed mb-6">
              We apologise for the inconvenience. You can try again or reload the page.
            </p>

            {/* Error detail (development aid) — only shown when an error message is available */}
            {error?.message && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-sm px-4 py-3">
                <p className="text-xs font-mono text-red-700 dark:text-red-400 break-words leading-relaxed">
                  {error.message}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={this.resetBoundary}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium border border-light-border dark:border-dark-border bg-light-surface dark:bg-dark-surface text-slate-700 dark:text-dark-text hover:bg-light-sidebar dark:hover:bg-dark-sidebar transition-colors duration-150"
              >
                <RotateCcw className="w-4 h-4" aria-hidden="true" />
                Try again
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-white hover:bg-primary-700 transition-colors duration-150"
              >
                <RefreshCw className="w-4 h-4" aria-hidden="true" />
                Reload page
              </button>
            </div>
          </div>

          {/* Footer note */}
          <p className="mt-4 text-center text-slate-600 dark:text-dark-text-soft text-xs">
            If this keeps happening, please contact your administrator.
          </p>
        </div>
      </div>
    );
  }
}
